import { Button, Modal, Select } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";

const formatDeviceLabel = (label: string) => {
  const regex = /\(([0-9a-fA-F]{4}:[0-9a-fA-F]{4})\)/;

  if (regex.test(label)) {
    return label.replace(regex, "").trim();
  }
  return label;
};

export default function MicrophoneTest({
  open,
  onCancel,
  devices,
}: {
  open: boolean;
  onCancel: () => void;
  devices: MediaDeviceInfo[];
}) {
  const audioContextRef = useRef<AudioContext>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayFrequencyRef = useRef<any | null>(null); // Only need frequency data for volume bars
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);

  const [current, setCurrent] = useState("default");

  const [isTestInput, setIsTestInput] = useState(false);
  const setupAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;

      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayFrequencyRef.current = new Uint8Array(bufferLength);
    }
  }, []);

  const barCount = 25;

  const setupVolumeBars = useCallback(() => {
    const volumeCanvas = volumeCanvasRef.current;

    const barWidth = volumeCanvas!.width / (barCount * 2 - 1);

    if (volumeCanvas) {
      const volumeCtx = volumeCanvas.getContext("2d");
      volumeCtx!.clearRect(0, 0, volumeCanvas.width, volumeCanvas.height);
      if (volumeCtx) {
        volumeCtx.fillStyle = "rgb(240, 240, 240)";
        for (let i = 0; i < barCount; i++) {
          const xPos = i * (barWidth * 2);
          const height = volumeCanvas.height;
          volumeCtx!.fillRect(xPos, 0, barWidth, height);
        }
      } else {
        console.error("Failed to get canvas context for volume canvas.");
      }
    } else {
      console.error("Volume canvas is not available.");
    }
  }, []);

  const volumeCanvasRef = useRef<HTMLCanvasElement>(null); // Ref for the volume bar canvas

  const drawVolumeBars = useCallback(() => {
    if (!analyserRef.current || !volumeCanvasRef.current) {
      animationFrameIdRef.current = requestAnimationFrame(drawVolumeBars);
      return;
    }

    analyserRef.current.getByteFrequencyData(dataArrayFrequencyRef.current!);

    const volumeCanvas = volumeCanvasRef.current;
    const volumeCtx = volumeCanvas.getContext("2d");

    volumeCtx!.clearRect(0, 0, volumeCanvas.width, volumeCanvas.height);

    const barWidth = volumeCanvas.width / (barCount * 2 - 1);

    let sumAmplitude = 0;
    for (let i = 0; i < dataArrayFrequencyRef.current!.length; i++) {
      sumAmplitude += dataArrayFrequencyRef.current![i];
    }
    const averageAmplitude =
      sumAmplitude / dataArrayFrequencyRef.current!.length;
    const normalizedAmplitude = averageAmplitude / 255;

    const count = (400 * normalizedAmplitude) / barWidth;

    for (let i = 0; i < barCount; i++) {
      if (i <= count) {
        volumeCtx!.fillStyle = "rgb(76, 175, 80)";
      } else {
        volumeCtx!.fillStyle = "rgb(240, 240, 240)";
      }
      const xPos = i * (barWidth * 2);

      const height = volumeCanvas.height;
      volumeCtx!.fillRect(xPos, 0, barWidth, height);
    }

    animationFrameIdRef.current = requestAnimationFrame(drawVolumeBars);
  }, []);

  const getMicrophoneInput = useCallback(async () => {
    try {
      if (
        !audioContextRef.current ||
        audioContextRef.current.state === "suspended"
      ) {
        setupAudioContext();
        await audioContextRef.current.resume();
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: current },
      });

      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }

      sourceRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);

      drawVolumeBars();
      console.log("Microphone input started!");
    } catch (err) {
      console.error("Error accessing microphone:", err);
    }
  }, [current, setupAudioContext, drawVolumeBars]);

  const stopMicrophoneInput = useCallback(() => {
    if (sourceRef.current) {
      const tracks = sourceRef.current.mediaStream.getTracks();
      tracks.forEach((track) => track.stop());
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.suspend();
    }
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    console.log("Microphone input stopped.");
  }, []);


  const destroy = useCallback(() => {
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      audioContextRef.current.close().then(() => {
        console.log("AudioContext closed.");
      });
    }
    animationFrameIdRef.current = null;
  }, []);

  useEffect(() => {
    if (open) {
      setupAudioContext();
      setupVolumeBars();
    } else {
      destroy();
    }

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      if (sourceRef.current) {
        const tracks = sourceRef.current.mediaStream.getTracks();
        tracks.forEach((track) => track.stop());
        sourceRef.current.disconnect();
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
      }
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close().then(() => {
          console.log("AudioContext closed.");
        });
        audioContextRef.current = null;
      }

      destroy();
    };
  }, [open]);

  const handleTestInput = async () => {
    if (isTestInput) {
      stopMicrophoneInput();
      setupVolumeBars();
    } else {
      getMicrophoneInput();
    }
    setIsTestInput(!isTestInput);
  };

  const cancel = () => { 
    onCancel();
    if (isTestInput) {
      stopMicrophoneInput();
      setupVolumeBars();
      setIsTestInput(false);
      destroy();
    }
  }

  return (
    <Modal
      title="测试麦克风"
      open={open}
      mask={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      wrapClassName={"test-modal-wrap"}
      onCancel={cancel}
    >
      <div>
        <Select
          placeholder="请选择设备"
          onChange={(v) => setCurrent(v)}
          value={current}
          disabled={!devices.length || isTestInput}
          style={{ width: "360px" }}
        >
          {devices.map((d) => (
            <Select.Option value={d.deviceId} key={d.deviceId}>
              {formatDeviceLabel(d.label) || d.deviceId}
            </Select.Option>
          ))}
        </Select>

        <Button className="ml10" onClick={handleTestInput}>
          {isTestInput ? "停止测试" : "测试"}
        </Button>
      </div>

      <div    className="mt10">输出</div>
      <canvas
        className="mt10"
        ref={volumeCanvasRef}
        width="350"
        height="20"
        style={{ backgroundColor: "transparent" }}
      ></canvas>
    </Modal>
  );
}
